import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FaMagic, FaBoxOpen, FaTimes, FaPlus } from 'react-icons/fa';
import { Link, useLocation } from 'react-router-dom';
import { API } from '../App';
import RecipeCard from '../components/RecipeCard';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import OnboardingTooltip from '../components/OnboardingTooltip';
import ProFeatureLocked from '../components/ProFeatureLocked';
import { getMatchPageSteps, isTourCompleted, markTourCompleted, TOUR_KEYS } from '../utils/onboarding';
import { useTranslation } from 'react-i18next';
import { trackEvent, ANALYTICS_EVENTS } from '../utils/analytics';
import { hasPremiumAccess } from '../config/subscriptions';

const MatchFinderPage = ({ sessionId }) => {
  const { user, updateCompletedTours } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pantryCount, setPantryCount] = useState(0);
  const [pantryItems, setPantryItems] = useState([]);
  const [currentTourStep, setCurrentTourStep] = useState(-1);
  const [previousPantryCount, setPreviousPantryCount] = useState(0);
  const [onlyComplete, setOnlyComplete] = useState(false);

  useEffect(() => {
    checkPantry();
    
    // Restore previous match results if they exist
    const savedMatches = sessionStorage.getItem(`matches_${sessionId}`);
    if (savedMatches) {
      try {
        setMatches(JSON.parse(savedMatches));
      } catch (e) {
        console.error('Error restoring matches:', e);
      }
    }
  }, [sessionId]);

  // Clear matches when pantry changes
  useEffect(() => {
    if (previousPantryCount !== pantryCount && previousPantryCount !== 0) {
      console.log(`Pantry changed from ${previousPantryCount} to ${pantryCount} - clearing matches`);
      setMatches(null);
      sessionStorage.removeItem(`matches_${sessionId}`);
      
      // Notify user that they should re-run match
      if (pantryCount > 0) {
        toast.info(t('matchFinder.pantryUpdated', 'Dine ingredienser er opdateret! Klik "Find matches" for at se nye resultater.'), {
          duration: 4000
        });
      }
    }
    setPreviousPantryCount(pantryCount);
  }, [pantryCount]);


  // Auto-run match if coming from pantry page with autoMatch flag
  useEffect(() => {
    if (location.state?.autoMatch && pantryCount > 0 && !loading && !matches) {
      console.log('[Match] Auto-running match from pantry page');
      findMatches();
    }
  }, [location.state, pantryCount, loading]);

  // Start tour for first-time users
  useEffect(() => {
    if (hasPremiumAccess(user) && !isTourCompleted(TOUR_KEYS.MATCH, user)) {
      console.log('[Tour] Starting match finder tour...');
      setTimeout(() => {
        setCurrentTourStep(0);
      }, 1500);
    }
  }, [user]);

  const handleTourNext = () => {
    setCurrentTourStep(prev => prev + 1);
  };

  const handleTourSkip = () => {
    markTourCompleted(TOUR_KEYS.MATCH, API, updateCompletedTours);
    setCurrentTourStep(-1);
  };

  const handleTourFinish = () => {
    markTourCompleted(TOUR_KEYS.MATCH, API, updateCompletedTours);
    setCurrentTourStep(-1);
    toast.success(t('matchFinder.tourComplete', 'Match-Finder guide færdig!'));
  };


  const checkPantry = async () => {
    try {
      const response = await axios.get(`${API}/pantry/${sessionId}`);
      setPantryItems(response.data);
      setPantryCount(response.data.length);
    } catch (error) {
      console.error('Error checking pantry:', error);
    }
  };

  const removeIngredient = async (itemId) => {
    try {
      await axios.delete(`${API}/pantry/${sessionId}/${itemId}`);
      toast.success(t('matchFinder.ingredientRemoved', 'Ingrediens fjernet'));
      
      // Clear matches since pantry changed
      setMatches(null);
      sessionStorage.removeItem(`matches_${sessionId}`);
      
      checkPantry(); // Refresh pantry count
    } catch (error) {
      console.error('Error removing ingredient:', error);
      toast.error(t('matchFinder.removeError', 'Kunne ikke fjerne ingrediens'));
    }
  };

  const findMatches = async () => {
    if (pantryCount === 0) {
      toast.error(t('matchFinder.addIngredientsFirst', 'Tilføj ingredienser først!'));
      return;
    }

    setLoading(true);
    try {
      // Add cache busting
      const response = await axios.post(`${API}/match?_t=${Date.now()}`, {
        session_id: sessionId
      });
      
      console.log('Match Results:', {
        can_make: response.data.can_make_now?.length,
        almost: response.data.almost?.length,
        need_more: response.data.need_more?.length,
        pantry_items: pantryItems.map(i => i.ingredient_name)
      });
      
      setMatches(response.data);

      // Save matches to sessionStorage
      sessionStorage.setItem(`matches_${sessionId}`, JSON.stringify(response.data));

      // Funnel analytics:
      //   `match_search_run` fires once per match request (how often is the
      //   feature used? does adding more pantry items convert to more searches?)
      //   `ingredient_match` fires per successful "can make now" hit so we
      //   can see which recipes the pantry tends to unlock.
      const canMake = response.data.can_make_now || [];
      const almost = response.data.almost || [];
      const triggeredByAuto = !!location.state?.autoMatch;
      trackEvent(ANALYTICS_EVENTS.MATCH_SEARCH_RUN, {
        pantry_count: pantryCount,
        results_can_make: canMake.length,
        results_almost: almost.length,
        results_total: canMake.length + almost.length + (response.data.need_more?.length || 0),
        source: triggeredByAuto ? 'pantry-auto' : 'manual',
      });
      for (const r of canMake.slice(0, 20)) {
        trackEvent(ANALYTICS_EVENTS.INGREDIENT_MATCH, {
          recipe_id: r.recipe?.id || r.id,
          recipe_name: r.recipe?.name || r.name,
          recipe_type: r.recipe?.type || r.type,
          match_quality: 'can_make_now',
          match_score: r.match_score ?? r.score ?? null,
        });
      }

      toast.success(t('matchFinder.foundMatches', `Fandt ${response.data.can_make_now?.length || 0} matches!`, { count: response.data.can_make_now?.length || 0 }));
    } catch (error) {
      console.error('Error finding matches:', error);
      toast.error(t('matchFinder.searchError', 'Kunne ikke finde matches'));
    } finally {
      setLoading(false);
    }
  };

  // Guest users may now BROWSE and USE the Match Finder freely — paywall
  // only kicks in at the value-extraction boundary (saving, opening Pro
  // recipes, etc). This is the "try the value first" change requested
  // after we noticed guests bounced off a hard upgrade wall before they
  // ever got to see what Match does.

  return (
    <div className="space-y-6 fade-in" data-testid="match-finder-page">
      {/* Onboarding Tour */}
      <OnboardingTooltip
        steps={getMatchPageSteps()}
        currentStep={currentTourStep}
        onNext={handleTourNext}
        onSkip={handleTourSkip}
        onFinish={handleTourFinish}
      />
      
      <div>
        <h1 className="text-4xl font-bold mb-2">{t('matchFinder.title')}</h1>
        <p className="text-gray-600">{t('matchFinder.subtitle')}</p>
      </div>

      {/* Ingredients Display & Actions */}
      <div className="bg-gradient-to-br from-cyan-50 to-coral-50 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h3 className="text-2xl font-bold mb-1">{t('matchFinder.yourIngredients')}</h3>
            <p className="text-gray-600">
              {t(pantryCount === 1 ? 'matchFinder.ingredientCount' : 'matchFinder.ingredientCountPlural', { count: pantryCount })}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Link to="/pantry" className="w-full sm:w-auto">
              <Button variant="outline" size="sm" className="w-full" data-tour="add-pantry-button">
                <FaPlus className="mr-2" /> {t('matchFinder.addMore')}
              </Button>
            </Link>
            <Button
              onClick={findMatches}
              disabled={loading || pantryCount === 0}
              data-testid="find-match-button"
              data-tour="find-matches-button"
              className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 w-full sm:w-auto"
            >
              <FaMagic className="mr-2" />
              {loading ? t('matchFinder.searching') : t('matchFinder.findMatches')}
            </Button>
          </div>
        </div>

        {/* Ingredient Pills */}
        {pantryCount > 0 ? (
          <div className="flex flex-wrap gap-2 mt-4">
            {pantryItems.map((item) => (
              <div
                key={item.id}
                className="bg-white rounded-full px-4 py-2 flex items-center gap-2 shadow-sm border border-gray-200 hover:border-cyan-300 transition-all group"
              >
                <span className="font-medium text-gray-800">{item.ingredient_name}</span>
                <button
                  onClick={() => removeIngredient(item.id)}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                  title="Fjern ingrediens"
                >
                  <FaTimes className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 bg-white/50 rounded-lg mt-4">
            <FaBoxOpen className="mx-auto text-4xl text-gray-300 mb-3" />
            <p className="text-gray-500 mb-4">{t('matchFinder.noIngredients')}</p>
            <Link to="/pantry">
              <Button className="bg-cyan-500 hover:bg-cyan-600">
                <FaPlus className="mr-2" /> {t('matchFinder.addIngredients', 'Tilføj Ingredienser')}
              </Button>
            </Link>
          </div>
        )}
      </div>

      {/* Hint Card - Only show if no matches yet */}
      {!matches && pantryCount > 0 && (
        <div className="bg-white rounded-lg p-6 border-l-4 border-cyan-500 shadow-sm">
          <h3 className="font-bold text-gray-800 mb-2 flex items-center gap-2">
            <span className="text-2xl">💡</span>
            Klar til at finde matches?
          </h3>
          <p className="text-gray-600">
            Klik på "Find matches" for at se hvilke opskrifter du kan lave med dine ingredienser
          </p>
        </div>
      )}

      {/* Results */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      )}

      {matches && (
        <div className="space-y-8">
          {/* Filter toggle */}
          {((matches.can_make_now?.length || 0) + (matches.almost?.length || 0)) > 0 && (
            <label
              className="inline-flex items-center gap-2 cursor-pointer select-none text-sm text-gray-700 bg-white border border-gray-200 rounded-full px-3 py-1.5 shadow-sm"
              data-testid="match-only-complete-toggle"
            >
              <input
                type="checkbox"
                checked={onlyComplete}
                onChange={(e) => setOnlyComplete(e.target.checked)}
                className="w-4 h-4 accent-emerald-500"
              />
              Vis kun komplette matches
            </label>
          )}

          {/* All recipes with user's ingredients - sorted by match count */}
          {(matches.can_make_now && matches.can_make_now.length > 0) || (!onlyComplete && matches.almost && matches.almost.length > 0) ? (
            <>
              {/* Perfect matches first */}
              {matches.can_make_now && matches.can_make_now.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <h2 className="text-2xl font-bold">Komplet match ✓</h2>
                    <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {matches.can_make_now.length}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {matches.can_make_now.map((item) => (
                      <RecipeCard
                        key={item.recipe.id}
                        recipe={item.recipe}
                        sessionId={sessionId}
                        showMatchInfo={item}
                      />
                    ))}
                  </div>
                </div>
              )}
              
              {/* Recipes with some ingredients */}
              {!onlyComplete && matches.almost && matches.almost.length > 0 && (
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-3 h-3 bg-cyan-500 rounded-full"></div>
                    <h2 className="text-2xl font-bold">Du har de fleste ingredienser</h2>
                    <span className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm font-semibold">
                      {matches.almost.length}
                    </span>
                  </div>
                  <p className="text-gray-600 mb-4">Sorteret efter højeste match — klik for at se hvad du mangler</p>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {matches.almost.map((item) => (
                      <RecipeCard
                        key={item.recipe.id}
                        recipe={item.recipe}
                        sessionId={sessionId}
                        showMatchInfo={item}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-gray-200">
              <div className="text-6xl mb-4">🔍</div>
              <h3 className="text-2xl font-bold text-gray-800 mb-3">
                {onlyComplete && matches.almost?.length > 0
                  ? 'Ingen komplette matches – men der findes opskrifter du næsten kan lave'
                  : 'Ingen opskrifter bruger nogle af dine ingredienser endnu'}
              </h3>
              <p className="text-gray-600 mb-6">
                {onlyComplete
                  ? 'Fjern markeringen i “Vis kun komplette matches” for at se delvise matches'
                  : 'Prøv at tilføje andre ingredienser til dit pantry'}
              </p>
            </div>
          )}
        </div>
      )}

      {!matches && !loading && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🔍</div>
          <h3 className="text-xl font-bold mb-2">
            {t('matchFinder.readyToMatch')}
          </h3>
          <p className="text-gray-600 text-center">
            {t('matchFinder.clickToMatch')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MatchFinderPage;